import React, { useState, useEffect } from 'react';
import { Phone, MessageCircle, Mail, MapPin } from 'lucide-react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import CartSidebar from '../components/CartSidebar';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Contact = () => {
  const { isOpen: cartOpen, setIsOpen: setCartOpen } = useCart();
  const { settings } = useSettings();
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    axios.get(`${API}/banners`)
      .then(res => setBanner(res.data.find(b => b.type === 'contact')))
      .catch(console.error);
  }, []);

  const wa = (settings.whatsappNumber || '919001204207').replace(/\D/g, '');

  const cards = [
    {
      icon: <Phone size={24} />,
      label: 'Call Us',
      value: settings.phone || '+91 9001204207',
      href: `tel:${settings.phone}`,
      sub: 'Available 9 AM – 9 PM',
      testId: 'contact-call-card',
    },
    {
      icon: <MessageCircle size={24} />,
      label: 'WhatsApp',
      value: settings.whatsappNumber || '+91 9001204207',
      href: `https://wa.me/${wa}`,
      sub: 'Chat with us anytime',
      testId: 'contact-whatsapp-card',
    },
    {
      icon: <Mail size={24} />,
      label: 'Email Us',
      value: settings.email || 'info@apnidhanibakery.com',
      href: `mailto:${settings.email}`,
      sub: 'We reply within 24 hours',
      testId: 'contact-email-card',
    },
    {
      icon: <MapPin size={24} />,
      label: 'Visit Us',
      value: settings.address || 'Apni Dhani Bakery, Jaipur',
      href: `https://maps.google.com/?q=${encodeURIComponent(settings.address || 'Apni Dhani Bakery Jaipur')}`,
      sub: 'Mon–Sun: 8 AM – 10 PM',
      testId: 'contact-office-card',
    },
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="contact-page">
      <SEO
        title="Contact Us & Bakery Location"
        description="Get in touch with Apni Dhani Bakery in Sawai Madhopur, Rajasthan. Find our address, phone number, WhatsApp contact and business hours."
        path="/contact"
      />
      <Navbar onCartClick={() => setCartOpen(true)} />
      <PageBanner banner={banner} />

      <div className="max-w-5xl mx-auto px-6 py-14">
        <div className="text-center mb-10">
          <h2 className="font-playfair text-3xl font-bold text-[#1F1410]">How Can We Help?</h2>
          <p className="text-[#7A6A63] mt-2">Reach us through any of the channels below — we're always happy to hear from you.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="contact-cards">
          {cards.map(card => (
            <a
              key={card.label}
              href={card.href}
              target={card.href.startsWith('http') ? '_blank' : undefined}
              rel={card.href.startsWith('http') ? 'noreferrer' : undefined}
              className="group flex flex-col items-center text-center p-6 bg-white rounded-2xl border border-[#ECE0DA] hover:border-[#7A1125] hover:shadow-lg transition-all duration-200 card-hover"
              data-testid={card.testId}
            >
              <div className="w-14 h-14 rounded-full bg-[#7A1125]/10 group-hover:bg-[#7A1125] flex items-center justify-center text-[#7A1125] group-hover:text-white transition-all duration-200 mb-4">
                {card.icon}
              </div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#7A6A63] mb-1">{card.label}</p>
              <p className="font-playfair font-semibold text-[#1F1410] text-sm leading-snug break-all mb-1">{card.value}</p>
              <p className="text-[#7A6A63] text-xs">{card.sub}</p>
            </a>
          ))}
        </div>
      </div>

      <Footer />
      <CartSidebar isOpen={cartOpen} onClose={() => setCartOpen(false)} onCheckout={() => {}} />
    </div>
  );
};

export default Contact;
